import React, { useMemo, useState, useEffect } from 'react';
import { HStack, Input, Spinner, Text, VStack } from '@chakra-ui/react';
import ModalContainer from '@/components/molecules/ModalContainer/ModalContainer';
import { TUserActivationToggle } from '@/types/User.types';
import { useOrganizations } from '@/hooks/organization/organization';
import { useTableColumnSort } from '@/hooks/useTableColumnSort';
import { TOrganizationAdminListItem } from '@/types/Organization.types';
import CustomTable from '@/components/molecules/CustomTable';
import CustomButton from '@/components/atoms/CustomButton';
import colors from '@/theme/colors';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import useReassignDeactivatedUserToDifferentOrganization from '@/hooks/user/useReassignDeactivatedUserToDifferentOrganization';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  error?: string;
  user?: TUserActivationToggle;
}

const COLUMNS: string[] = ['Name', ''];
export type TAdminOrganizationsSortingColumn = (typeof COLUMNS)[number];

const SEARCH_TEXT_INIT = '';

const ReassignUserOrganizationModal = ({ isOpen, error, onClose, user }: ConfirmationModalProps) => {
  const [localSearchText, setLocalSearchText] = useState(SEARCH_TEXT_INIT);
  const [debouncedSearchText, setDebouncedSearchText] = useState(SEARCH_TEXT_INIT);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [selectedOrganization, setSelectedOrganization] = useState<TOrganizationAdminListItem | null>(null);

  const { sortingColumn, sortingDirection, updateSortingColumn } = useTableColumnSort<TAdminOrganizationsSortingColumn>(
    {
      defaultSortingColumn: 'Name',
    },
  );

  const { mutate: reassignDeactivatedUser, isPending: isReassigningDeactivatedUser } =
    useReassignDeactivatedUserToDifferentOrganization(() => {
      setIsConfirmationModalOpen(false);
      onClose();
    });

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(localSearchText);
      pagination.getFirstPage();
    }, 400);

    return () => clearTimeout(handler);
  }, [localSearchText]);

  const {
    isLoading,
    data: organizationsResponse,
    pagination,
    isRefetching,
  } = useOrganizations({
    sortingColumn,
    sortingDirection,
    searchText: debouncedSearchText, // ⬅️ Debounced search applied here
  });

  const organizationsTableData = organizationsResponse?.map((organization) => ({
    ...organization,
    id: organization._id,
  }));

  const data = { nodes: organizationsTableData ?? [] };

  const tableListItems = useMemo(
    () =>
      data.nodes.map((item) => ({
        id: item._id,
        cells: [
          item.name,
          <HStack width={'full'} justifyContent={'flex-end'}>
            <CustomButton
              isTransparent
              label={'Select'}
              hoverBackgroundColor="primary.500"
              labelColor={colors.text.mediumBlue}
              labelHoverColor={colors.extra.white}
              onClick={() => {
                setSelectedOrganization(item);
                setIsConfirmationModalOpen(true);
              }}
              height={'35px'}
              noShadow
            />
          </HStack>,
        ],
      })),
    [data.nodes],
  );

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title={'Reassign User to Different Organization'}
      modalBodyPadding={0}
      width="720px"
      footerContent={
        <HStack justifyContent="center" padding="20px" gap="10px">
          <CustomButton
            isTransparent
            label={'Cancel'}
            hoverBackgroundColor="primary.500"
            labelColor={colors.text.mediumBlue}
            labelHoverColor={colors.extra.white}
            onClick={onClose}
            noShadow
          />
        </HStack>
      }
    >
      <VStack spacing={'20px'}>
        <Text fontSize={'20px'} marginTop={'20px'} textAlign={'center'} whiteSpace={'pre-wrap'} width={'100%'}>
          {`Reassign user ${user?.firstName} ${user?.lastName} to a different organization.`}
        </Text>

        <Input
          className="custom-input"
          backgroundColor={'white'}
          placeholder="Search..."
          _placeholder={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
          value={localSearchText}
          onChange={(e) => setLocalSearchText(e.target.value)}
        />

        <HStack>
          <Text variant={'urbanistSmallBold'} color={'text.mediumGray'} padding={'5px'}>
            Minimum 3 characters
          </Text>
          {isLoading || isRefetching ? <Spinner /> : null}
        </HStack>

        {error && <Text variant={'error'}>{error}</Text>}

        <CustomTable<TOrganizationAdminListItem[], TAdminOrganizationsSortingColumn>
          isLoading={isLoading}
          dataWithId={data.nodes}
          isRefetching={isRefetching}
          pagination={pagination}
          tableListItems={tableListItems}
          isSearching={isLoading || isRefetching}
          noItemsMessage="No organizations created yet"
          columns={COLUMNS}
          sortingColumn={sortingColumn}
          updateSortingColumn={updateSortingColumn}
          sortingDirection={sortingDirection}
          hasRowSelect={false}
          navigateOnRowSelect={false}
          fullWidth
          marginTop={'8px'}
        />
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onCancel={() => {
            setIsConfirmationModalOpen(false);
            setSelectedOrganization(null);
          }}
          title="Confirm Reassignment"
          description={`Are you sure you want to reassign user ${user?.firstName} ${user?.lastName} to organization ${selectedOrganization?.name}?`}
          confirmButtonText="Confirm"
          confirmLoading={isReassigningDeactivatedUser}
          onConfirm={() => {
            if (user && selectedOrganization) {
              reassignDeactivatedUser({ userId: user.id, organizationId: selectedOrganization._id });
            }
          }}
        />
      </VStack>
    </ModalContainer>
  );
};

export default ReassignUserOrganizationModal;
